import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef, Inject, Optional, inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HotelService } from '../../core/services/hotel.service';
import { Hotel } from '../../core/interfaces/hotel';
import { HotelSettingService } from '../../core/services/hotel-setting.service';
import { HotelStatus } from '../../core/interfaces/hotel-settings';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap, takeUntil } from 'rxjs/operators';
import { ConfirmationService } from '../../core/services/confirmation.service';
import { TranslateService } from '@ngx-translate/core';
import { PermissionService } from '../../core/services/permission.service';

/**
 * Who navigates after a hotel is picked.
 * - 'reload': the dialog itself does a full page load of /<id>/dashboard (default,
 *   used by the topbar, RedirectLoggedGuard and HotelSelectionUrlGuard)
 * - 'guard': HotelSelectionGuard opened the dialog while the router is still on
 *   the previous route (login, verify-2fa) and navigates with its own UrlTree
 *   once the hotel is initialised, so the dialog must not reload
 */
export type HotelSelectionNavigation = 'reload' | 'guard';

/**
 * Data passed into the dialog: hotels pre-loaded by the guard to avoid a
 * redundant API call, and the navigation mode.
 */
export interface HotelSelectionDialogData {
  hotels?: Hotel[];
  total?: number;
  navigation?: HotelSelectionNavigation;
}

@Component({
    selector: 'app-hotel-selection-dialog',
    templateUrl: './hotel-selection-dialog.component.html',
    styleUrls: ['./hotel-selection-dialog.component.scss'],
    standalone: false
})
export class HotelSelectionDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  // Search debouncing
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  private confirmationService = inject(ConfirmationService);
  private translate = inject(TranslateService);
  public permissionService = inject(PermissionService);
  isSearching = false;
  searchTerm = '';

  protected readonly parseInt = parseInt;
  hotels: Hotel[] = [];
  rooms = [
    { id: 1, hide: 0 }, // Online
    { id: 2, hide: 1 }, // Hidden
    { id: 3, hide: 2 }, // Offline
    { id: 4, hide: 99 } // Offline
  ];
  hotelStatus = 1;
  filteredHotels: Hotel[] = [];
  filters = {
    page: 1,
    per_page: 24, // Load fewer hotels initially to ensure scrolling
    total: 0,
    loaded_component: false,
    loading_data: false,
    options: JSON.stringify({ sizes: [10, 25, 50, 100] }),
    only_archived: false,
  };
  loading = false; // Flag to prevent multiple simultaneous requests
  hotelId: string | null = null;
  currentHotelId: string | null = null; // Currently selected hotel
  pendingCounts: Record<number, number> = {}; // Map of hotel_id to pending count

  constructor(
    public dialogRef: MatDialogRef<HotelSelectionDialogComponent>,
    public hotelService: HotelService,
    private hotelSetting: HotelSettingService,
    @Optional() @Inject(MAT_DIALOG_DATA) private preloadedData: HotelSelectionDialogData | null
  ) {}

  ngOnInit(): void {
    // Get current hotel ID from localStorage
    this.currentHotelId = localStorage.getItem('lastHotelId');

    // Use pre-loaded data from guard if available, otherwise fetch
    if (this.preloadedData?.hotels?.length) {
      this.hotels = this.preloadedData.hotels;
      this.filters.total = this.preloadedData.total ?? this.hotels.length;
      this.filteredHotels = this.processHotelsData(this.hotels);
    } else {
      this.loadHotels();
    }

    this.fetchPendingCounts();
    this.setupDebouncedSearch();
  }

  private setupDebouncedSearch(): void {
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(value => {
        this.searchTerm = value;
        this.isSearching = true;
      }),
      switchMap(value => {
        const filterValue = value.trim().toLowerCase();

        if (!filterValue) {
          // Reset to first page when clearing search
          this.filters.page = 1;
          this.filters.per_page = 24;
          this.hotels = []; // Clear for fresh load
          return this.hotelService.getHotels(this.buildParams());
        }

        // Search with all results
        const perPage = this.filters.total && this.filters.total > 0
          ? this.filters.total
          : this.filters.per_page;
        return this.hotelService.getHotels(this.buildParams({ page: 1, per_page: perPage, search: filterValue }));
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        this.hotels = response.data ?? [];
        this.filteredHotels = this.processHotelsData(this.hotels);
        this.filters.total = response.total ?? 0;
        this.isSearching = false;
      },
      error: () => {
        this.isSearching = false;
      }
    });
  }

  private processHotelsData(hotels: Hotel[]): Hotel[] {
    const processed = hotels.map(hotel => {
      // Hotel search page status is derived from is_published and hide_hotel:
      //   hide_hotel = 1              → Hidden   (2)
      //   is_published = 1            → Published (1)
      //   is_published = 0            → Offline  (0)
      let derivedStatus: HotelStatus;
      if (hotel.hide_hotel === 1) {
        derivedStatus = HotelStatus.HIDDEN;
      } else if (hotel.is_published === 1) {
        derivedStatus = HotelStatus.PUBLISHED;
      } else {
        derivedStatus = HotelStatus.OFFLINE;
      }

      // localStorage is written by hotel-settings page when the user changes status mid-session.
      // It stores hotel_status (0/1/2), so we use it directly when present.
      const storedStatus = localStorage.getItem(`hotel_status_${hotel.id}`);
      const storedHide = localStorage.getItem(`hide_hotel_${hotel.id}`);

      const hotelStatus = storedStatus !== null ? Number(storedStatus) : derivedStatus;
      const hideHotel = storedHide !== null ? Number(storedHide) : hotel.hide_hotel;

      // Get real pending count from API data
      const pendingCount = this.pendingCounts[hotel.id] ?? 0;

      // Extract completeness_percentage from nested settings object
      const hotelWithSettings = hotel as Hotel & { settings?: { completeness_percentage?: number } };
      const completenessPercentage = hotelWithSettings.settings?.completeness_percentage ?? hotel.completeness_percentage ?? 0;

      return {
        ...hotel,
        hotel_status: hotelStatus,
        hide_hotel: hideHotel,
        pending_count: pendingCount,
        completeness_percentage: completenessPercentage
      };
    });

    // Sort: Online (Published) first, then Hidden, then Offline
    const statusOrder: Record<number, number> = {
      [HotelStatus.PUBLISHED]: 0,
      [HotelStatus.HIDDEN]: 1,
      [HotelStatus.OFFLINE]: 2,
    };

    return processed.sort((a, b) => {
      const orderA = statusOrder[a.hotel_status ?? HotelStatus.OFFLINE] ?? 2;
      const orderB = statusOrder[b.hotel_status ?? HotelStatus.OFFLINE] ?? 2;
      return orderA - orderB;
    });
  }

  ngAfterViewInit(): void {
    // Auto-focus search input when dialog opens
    setTimeout(() => {
      this.searchInput?.nativeElement?.focus();
    }, 100);
  }

  getRoomStatus(hide: number): string {
    if (hide === 0) {
      return 'Online';
    } else if (hide === 1) {
      return 'Hidden';
    } else {
      return 'Offline';
    }
  }

  loadHotels(): void {
    if (this.loading) return;
    this.loading = true;

    this.hotelService.getHotels(this.filters).subscribe({
      next: (response) => {
        // Append new hotels
        this.hotels = [...this.hotels, ...(response.data ?? [])];
        this.filteredHotels = this.processHotelsData(this.hotels);
        this.filters.total = response.total ?? 0;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.confirmationService.swalError(this.translate.instant('ERRORS.LOAD_HOTELS_FAILED'));
      }
    });
  }


  navigateWithHotelId(hotelId: string): void {
    window.location.href = `/${hotelId}/dashboard`;
  }

  onSelect(hotelId: string): void {
    // Update localStorage BEFORE page reload to prevent race condition
    // This ensures restoreLastHotel() will use the correct hotel ID after reload
    localStorage.setItem('lastHotelId', hotelId);

    this.dialogRef.close(hotelId);

    // Opened by HotelSelectionGuard: the guard initialises the hotel and
    // navigates with its UrlTree. A reload here would win over that and leave
    // the login or verify-2fa screen visible until the new page is up (HD-2013).
    if (this.isOpenedByGuard()) {
      return;
    }
    this.navigateWithHotelId(hotelId);
  }

  private isOpenedByGuard(): boolean {
    return this.preloadedData?.navigation === 'guard';
  }

  close(): void {
    this.dialogRef.close();
  }

  onScroll(event: Event): void {
    const element = event.target as HTMLElement;
    if (!this.loading && element.scrollHeight - element.scrollTop <= element.clientHeight * 1.2) {
      if (this.filters.page * this.filters.per_page < this.filters.total) {
        this.filters.page++;
        this.loadHotels();
      }
    }
  }

  onImageError(event: Event): void {
    (event.target as HTMLImageElement).src = 'assets/images/image_placeholder.jpg';
  }

  // Add this helper in your component class
  private buildParams(extras: Record<string, string | number | boolean> = {}): Record<string, string | number | boolean> {
    const params: Record<string, string | number | boolean | null | undefined> = { ...this.filters, ...extras };
    // remove null/undefined/empty-string so they don't appear in the URL
    const cleanedParams: Record<string, string | number | boolean> = {};
    Object.keys(params).forEach((k) => {
      const val = params[k];
      if (val !== undefined && val !== null && val !== '') {
        cleanedParams[k] = val as string | number | boolean;
      }
    });
    return cleanedParams;
  }

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchSubject.next(value);
  }

  toggleOnlyArchived(onlyArchived: boolean): void {
    this.filters.only_archived = onlyArchived;
    this.filters.page = 1;
    this.filters.per_page = 24;
    this.hotels = [];
    this.isSearching = true;

    this.hotelService.getHotels(this.buildParams({ page: 1, per_page: 24, search: this.searchTerm.trim() }))
      .subscribe({
        next: (response) => {
          this.hotels = response.data ?? [];
          this.filteredHotels = this.processHotelsData(this.hotels);
          this.filters.total = response.total ?? 0;
          this.isSearching = false;
        },
        error: () => {
          this.isSearching = false;
        }
      });
  }

  getCompletenessClass(percentage: number): string {
    if (percentage < 50) return 'red-spinner';
    else if (percentage < 60 || percentage < 80) return 'yellow-spinner';
    else return 'green-spinner';
  }

  closeDialog(event: MouseEvent): void {
    event.stopPropagation();
    this.dialogRef.close();
  }

  /**
   * Fetch pending booking counts for all hotels from API
   */
  private fetchPendingCounts(): void {
    this.hotelService.getAllPendingBookingsCounts()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.pendingCounts = response.data?.counts ?? {};
          // Re-process hotels to update pending counts
          if (this.hotels.length > 0) {
            this.filteredHotels = this.processHotelsData(this.hotels);
          }
        },
        error: () => {
          // Silently fail - hotels will show without pending counts
          this.pendingCounts = {};
        }
      });
  }

  goToBookings(hotelId: number, event: MouseEvent): void {
    event.stopPropagation();
    localStorage.setItem('lastHotelId', hotelId.toString());
    this.dialogRef.close(hotelId.toString());
    window.location.href = `/${hotelId}/bookings`;
  }

  goToAvailability(hotelId: number, event: MouseEvent): void {
    event.stopPropagation();
    localStorage.setItem('lastHotelId', hotelId.toString());
    this.dialogRef.close(hotelId.toString());
    window.location.href = `/${hotelId}/availability`;
  }

  getStatusLabel(status: number): string {
    switch (status) {
      case HotelStatus.PUBLISHED: return 'Online';
      case HotelStatus.HIDDEN: return 'Hidden';
      default: return 'Offline';
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
